/**
 * BannerResponsivePreview — WYSIWYG preview yang memakai `bannerRenderer` yang
 * SAMA dengan storefront. Menghindari duplikasi algoritma crop antara admin
 * dan storefront.
 */
import React, { useState } from "react";
import { imageUrl } from "@/lib/api";
import {
  computePreviewContainerClass,
  pickPreviewImage,
  getFocalObjectPosition,
  hasMobileOverride as hasMobileOverrideRule,
} from "@/lib/bannerRenderer";
import { Monitor, Smartphone } from "lucide-react";

const TABS = [
  { key: "desktop", label: "Desktop", Icon: Monitor },
  { key: "mobile", label: "Mobile", Icon: Smartphone },
];

export default function BannerResponsivePreview({
  image_url,
  mobile_image_url,
  focal_point,
  title,
  testId = "banner-responsive-preview",
}) {
  const [tab, setTab] = useState("desktop");
  const hasMobileOverride = hasMobileOverrideRule({ image_url, mobile_image_url });
  const src = pickPreviewImage({ breakpoint: tab, image_url, mobile_image_url });
  const containerCls = computePreviewContainerClass({
    breakpoint: tab,
    hasMobileOverride,
  });
  const focalCls = getFocalObjectPosition(focal_point);
  // Overlay text hint — beri tahu admin apakah mobile pakai override atau
  // fallback responsive dari primary banner.
  const mobileNote = tab === "mobile"
    ? hasMobileOverride
      ? "Mobile: pakai Banner Mobile Khusus (rasio 3:4)"
      : "Mobile: pakai Banner Utama dengan controlled crop"
    : null;
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-[#5C5C5C]">Preview responsif</span>
        <div className="inline-flex rounded-lg border border-[#E5E5E5] bg-white p-0.5" role="tablist">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${
                tab === key ? "bg-[#1A1A1A] text-white" : "text-[#5C5C5C] hover:bg-[#F5F5F5]"
              }`}
              data-testid={`${testId}-tab-${key}`}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>
      </div>
      <div
        className={`${containerCls} overflow-hidden rounded-xl border border-[#E5E5E5] bg-[#FAF9F6] ${tab === "mobile" ? "max-w-[220px] mx-auto" : "w-full"}`}
        data-testid={`${testId}-frame-${tab}`}
      >
        {src ? (
          <img
            src={imageUrl(src)}
            alt={`${title || "Banner"} — preview ${tab}`}
            className={`w-full h-full object-cover ${focalCls}`}
            data-testid={`${testId}-image-${tab}`}
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-xs text-[#9C9C9C]">
            Belum ada gambar
          </div>
        )}
      </div>
      {mobileNote && (
        <div className="text-[11px] text-[#5C5C5C]" data-testid={`${testId}-note-${tab}`}>
          {mobileNote}
        </div>
      )}
    </div>
  );
}
